// File: src/utils.js

// export function sanitize(s) {
//   return String(s).replace(/[\\/\s]+/g, "_");
// }

export function msToTime(ms) {
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

// export function buildQuestionPool(chaptersJson) {
//   const pool = [];
//   for (const chap of chaptersJson || []) {
//     for (const asg of chap.assignments || []) {
//       for (let i = 1; i <= asg.length; i++) {
//         pool.push({
//           chapter: chap.name,
//           assignment: asg.name,
//           qno: i,
//           assignmentLength: asg.length,
//         });
//       }
//     }
//   }
//   return pool;
// }

// export function sampleArray(arr, n) {
//   const res = [];
//   const used = new Set();
//   const limit = Math.min(n, arr.length);
//   while (res.length < limit) {
//     const idx = Math.floor(Math.random() * arr.length);
//     if (!used.has(idx)) {
//       used.add(idx);
//       res.push(arr[idx]);
//     }
//   }
//   return res;
// }

// export function deriveAnswerKeyCandidates(
//   chapter,
//   assignment,
//   qno,
//   assignmentLength
// ) {
//   const cand1 = `/images/${sanitize(chapter)}/${sanitize(assignment)}/${
//     assignmentLength + 1
//   }.png`;
//   const cand2 = `/images/${sanitize(chapter)}/${sanitize(
//     assignment
//   )}/ans_${qno}.png`;
//   const cand3 = `/images/${sanitize(chapter)}/${sanitize(
//     assignment
//   )}/answer_${qno}.png`;
//   return [cand1, cand2, cand3];
// }

// Utility: builds full pool of question entries given selected chapters

export function buildQuestionPool(chapters = []) {
  const pool = [];
  if (!Array.isArray(chapters)) return pool;

  chapters.forEach((ch) => {
    if (!ch || !Array.isArray(ch.assignments)) return;
    const chapterName = ch.name;
    const chapterDisplay = ch.displayName || ch.name;
    ch.assignments.forEach((asg) => {
      const assignmentName = asg.name;
      const assignmentDisplay = asg.displayName || asg.name;
      const length = Number(asg.length) || 0;
      for (let i = 1; i <= length; i++) {
        pool.push({
          chapter: chapterName,
          chapterDisplay,
          assignment: assignmentName,
          assignmentDisplay,
          qno: i,
          assignmentLength: length,
        });
      }
    });
  });

  return pool;
}

/**
 * Uniformly sample `n` items from `arr` without replacement.
 * Uses Fisher-Yates shuffle under the hood, returns a new array.
 * If n > arr.length, returns a shuffled copy of the whole array.
 */
export function sampleArray(arr = [], n = 1) {
  const len = arr.length;
  if (len === 0 || n <= 0) return [];
  // copy
  const copy = arr.slice();
  // fisher-yates shuffle (in-place)
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  const take = Math.min(n, copy.length);
  return copy.slice(0, take);
}

/**
 * Derive answer key candidate set for a question.
 *
 * NOTE:
 * - Your question-image already contains the option texts (A/B/C/D) according to your spec,
 *   so this function returns a sensible default candidate list.
 * - Keep this pure and synchronous (no network). If you want smarter candidates
 *   (e.g. pulled from answer_key.json), generate them where you have async access.
 *
 * Returns an array of candidate strings. For SMCQ/MMCQ => ['A','B','C','D'].
 * For NUMERICAL => an empty array (or you may return placeholder).
 */
export function deriveAnswerKeyCandidates(
  chapter,
  assignment,
  qno,
  assignmentLength
) {
  // This simple implementation returns the standard option labels.
  // If you want to vary candidates or include numeric guesses, change here.
  return ["A", "B", "C", "D"];
}

/**
 * Sanitize a string to be used in file paths / URLs under /images/.
 * - trims, lowercases
 * - replaces spaces with underscores
 * - removes characters unsafe for filenames (keeps letters, numbers, -, _, and .)
 */
export function sanitize(input) {
  if (input == null) return "";
  const s = String(input).trim().toLowerCase();
  // replace spaces with underscore
  const withUnderscores = s.replace(/\s+/g, "_");
  // remove characters except a-z0-9._-
  const cleaned = withUnderscores.replace(/[^a-z0-9._-]/g, "");
  return cleaned;
}

/**
 * Small helper: count total questions available given your selected-list shaped input.
 * Expects same shape as buildQuestionPool's input (chapters array).
 * Returns integer.
 */
export function countAvailableQuestions(chapters = []) {
  let total = 0;
  if (!Array.isArray(chapters)) return 0;
  chapters.forEach((ch) => {
    if (!ch || !Array.isArray(ch.assignments)) return;
    ch.assignments.forEach((asg) => {
      total += Number(asg.length) || 0;
    });
  });
  return total;
}

/**
 * Helper: pick `n` items from pool biased towards lower frequency.
 * Strategy:
 *  - bucket pool items by frequency (0,1,2,...).
 *  - start from smallest freq bucket, randomly pick from it until you fill n;
 *  - if bucket has more than needed, randomly choose required number from it.
 */
export function pickLowestBias(pool, n) {
  if (!Array.isArray(pool) || pool.length === 0) return [];
  const buckets = new Map(); // freq -> array of items
  let minFreq = Infinity;
  for (const item of pool) {
    const freq = Number(item._qfreq || 0);
    minFreq = Math.min(minFreq, freq);
    if (!buckets.has(freq)) buckets.set(freq, []);
    buckets.get(freq).push(item);
  }

  // sort frequencies ascending
  const freqs = Array.from(buckets.keys()).sort((a, b) => a - b);

  const picked = [];
  for (const f of freqs) {
    const items = buckets.get(f).slice(); // copy
    // shuffle items (Fisher-Yates)
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }
    // take as many as possible
    const need = n - picked.length;
    if (need <= 0) break;
    if (items.length <= need) {
      picked.push(...items);
    } else {
      picked.push(...items.slice(0, need));
    }
  }

  // if still less than n (shouldn't unless pool smaller), fill from pool randomly
  if (picked.length < n) {
    const remaining = pool.filter((p) => !picked.includes(p));
    const fill = sampleArray(remaining, n - picked.length);
    picked.push(...fill);
  }

  return picked.slice(0, n);
}
